import { useToast } from '@helix-ds/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { track } from '../../../utils/analytics'
import { isPrimaryConsentFlow } from '../../../utils/consentFlowHelper'
import { withSupportMessage } from '../../../utils/errorMessage'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import type { ConsentLanguage } from '../hooks/useConsentLanguage'
import { buildPrimaryDocumentContent, formatSignedAt } from './consentOptionsUtils'
import type { ConsentContext } from './types'

export function usePrimaryConsent(
  context: ConsentContext | null,
  flowStartedAt: number | null,
  onFlowCompleted?: () => void,
  language?: ConsentLanguage | null
) {
  const apiClient = useFamilyPortalApiClient()
  const navigate = useNavigate()
  const intl = useIntl()
  const { addNotification } = useToast()
  const [agreed, setAgreed] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submittedSignedAt, setSubmittedSignedAt] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFlowCompleted, setIsFlowCompleted] = useState(false)
  const flowStartTimeRef = useRef<number | null>(null)

  const contextConfirmed = context?.primaryConsentId != null || context?.primaryConsentSignedAt != null
  const confirmed = submitted || contextConfirmed
  // Return null (not a fallback string) when no date is available so the render falls back to a
  // localized "already recorded" message instead of a stale hardcoded string.
  const signedAt = submittedSignedAt ?? (contextConfirmed ? formatSignedAt(context.primaryConsentSignedAt) : null)

  useEffect(() => {
    if (flowStartedAt != null) {
      flowStartTimeRef.current = flowStartedAt
    }
  }, [flowStartedAt])

  useEffect(() => {
    setAgreed(false)
  }, [intl.locale])

  const showPrimaryFlowNotice = useMemo(() => isPrimaryConsentFlow(context?.consentFlow), [context?.consentFlow])

  async function confirm() {
    setError(null)
    setSaving(true)
    try {
      // `language` is optional here because it comes from an async config fetch (useConsentLanguage)
      // that can be loading or failed. It's only guaranteed non-null in practice because
      // ConsentOptions.tsx withholds the sign screen (and thus this confirm handler) until
      // consentLanguage has loaded successfully — see the `!consentLanguage` gate there. If that
      // gating ever changes, documentContent will silently stop being sent (request still
      // succeeds; consent_event.document_content just goes back to NULL) rather than erroring.
      const documentContent = language ? buildPrimaryDocumentContent(context, language, intl.locale) : undefined
      const res = await apiClient.post('/consents/primary', documentContent ? { documentContent } : {})
      if (res.ok) {
        setSubmitted(true)
        setSubmittedSignedAt(new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }))
        setIsFlowCompleted(true)
        onFlowCompleted?.()
        const flowCompletedPayload: Record<string, unknown> = {}
        if (flowStartTimeRef.current != null) {
          flowCompletedPayload.total_duration_ms = Date.now() - flowStartTimeRef.current
        }
        track('consent.primary_confirmed', { is_update: confirmed })
        track('consent.flow_completed', flowCompletedPayload)
        navigate('/consents/secondary')
        return
      }
      const data = await res.json().catch(() => ({}))
      const message =
        typeof data?.detail === 'string'
          ? data.detail
          : res.status === 404
            ? intl.formatMessage({
                id: 'consentOptions.error.noConsentContext',
                defaultMessage: 'No consent context found. Please refresh and try again.',
              })
            : intl.formatMessage({
                id: 'consentOptions.error.couldNotSaveConsent',
                defaultMessage: "We couldn't save your consent. Please try again.",
              })
      track('consent.primary_failed', { status: res.status })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'consentOptions.toast.couldNotSaveConsentTitle',
          defaultMessage: "Couldn't save consent",
        }),
        message: withSupportMessage(intl, message),
      })
    } catch {
      track('consent.primary_failed', { status: 'network_error' })
      setError(
        withSupportMessage(
          intl,
          intl.formatMessage({
            id: 'consentOptions.error.couldNotSaveConsent',
            defaultMessage: "We couldn't save your consent. Please try again.",
          })
        )
      )
    } finally {
      setSaving(false)
    }
  }

  return {
    agreed,
    setAgreed,
    confirmed,
    signedAt,
    saving,
    error,
    confirm,
    isFlowCompleted,
    showPrimaryFlowNotice,
  }
}
